import { useEffect, useState, useRef } from "react";
import { Trophy, X, Sparkles } from "lucide-react";

const TIER_COLORS = {
  bronze: "from-gold-500 to-gold-600",
  silver: "from-ink-100 to-ink-700",
  gold: "from-gold-500 to-gold-600",
  diamond: "from-sky to-gold-600",
};

export default function AchievementToast() {
  const [queue, setQueue] = useState([]); // unlock stack
  const queueRef = useRef([]);

  useEffect(() => {
    function onUnlock(e) {
      const ach = e.detail;
      if (!ach) return;
      // de-dupe in case multiple events fire for same id
      if (queueRef.current.some((q) => q.id === ach.id)) return;
      queueRef.current = [...queueRef.current, ach];
      setQueue([...queueRef.current]);
      // Auto-dismiss after 6s
      setTimeout(() => {
        queueRef.current = queueRef.current.filter((q) => q.id !== ach.id);
        setQueue([...queueRef.current]);
      }, 6000);
    }
    window.addEventListener("achievement:unlocked", onUnlock);
    return () => window.removeEventListener("achievement:unlocked", onUnlock);
  }, []);

  function dismiss(id) {
    queueRef.current = queueRef.current.filter((q) => q.id !== id);
    setQueue([...queueRef.current]);
  }

  if (queue.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[200] flex flex-col gap-2 max-w-sm w-[calc(100vw-2rem)] sm:w-80 pointer-events-none">
      {queue.map((a) => (
        <div
          key={a.id}
          className={`pointer-events-auto relative overflow-hidden rounded-2xl bg-gradient-to-br ${TIER_COLORS[a.tier] || TIER_COLORS.bronze} text-ink-900 p-4 shadow-2xl border-2 border-cream-300 animate-slide-up`}
          style={{ animation: "slideInRight 0.4s ease-out" }}
        >
          {/* Sparkle decorations */}
          <Sparkles className="absolute top-2 right-8 w-4 h-4 text-gold-300 animate-pulse" />
          <Sparkles className="absolute bottom-2 left-2 w-3 h-3 text-gold-300 animate-pulse" style={{ animationDelay: "0.3s" }} />

          <button
            onClick={() => dismiss(a.id)}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-cream-200 transition"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>

          <div className="flex items-start gap-3">
            <div className="text-4xl flex-shrink-0 animate-bounce">{a.icon}</div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 mb-0.5">
                <Trophy className="w-3.5 h-3.5 text-gold-300" />
                <p className="text-[10px] font-black uppercase tracking-wider text-gold-300">Achievement Unlocked!</p>
              </div>
              <h4 className="font-black text-base leading-tight">{a.title}</h4>
              <p className="text-xs text-ink-800 mt-0.5 leading-snug">{a.desc}</p>
              <p className="text-[10px] uppercase tracking-wider font-bold mt-1.5 opacity-75">{a.tier} · {a.category}</p>
            </div>
          </div>
        </div>
      ))}

      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(30px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
